import { useMemo, useState } from 'react'
import { Button, Card, Flex } from 'ui'
import { AddWorkerToListModal } from 'components/AddWorkerToListModal'
import styled from 'styled-components'
import { ModalLink } from 'components/ModalLink'
import { LinkProps } from 'react-router-dom'
import { WorkerCardContent } from './WorkerCardContent'
import { useWorkerSearch } from './useWorkerSearch'

const CardContainer = styled(Card).attrs({
  as: ModalLink,
  flexWrap: 'wrap',
  flexDirection: 'row',
  alignItems: 'flex-start',
  my: 3,
  borderWidth: 0,
  color: 'text',
})<LinkProps>`
  ${({ theme: { colors } }) => `
    &:hover {
      position: relative;
      text-decoration: none;
      color: ${colors.text};
    }
  `}
`

interface Props {
  data: {
    id: number
    name: string
    numShiftsForCompany: number
    rating: number
    status: string
    workForCompany: {
      startedAt: string | null
    }[]
    statusForCompany: string | null
    profilePicUrl: string
    rosters: { id: number; name: string }[]
  }
}

export function WorkerCard(props: Props) {
  const [isRosterSelectorOpen, setIsRosterSelectorOpen] = useState(false)
  const { updateWorkerRosters } = useWorkerSearch()
  const rosters = props.data.rosters ?? []
  const listText = useMemo(() => {
    if (rosters.length === 0) {
      return '+ Add to List'
    }

    return `${rosters[0].name}${
      rosters.length > 1 ? ` +${rosters.length - 1}` : ''
    }`
  }, [rosters])

  const data = useMemo(() => {
    return {
      id: props.data.id,
      name: props.data.name,
      numShiftsForCompany: props.data.numShiftsForCompany,
      rating: props.data.rating,
      status: props.data.status,
      workForCompany: props.data.workForCompany,
      statusForCompany: props.data.statusForCompany,
      profilePicUrl: props.data.profilePicUrl,
      rosters:
        props.data.rosters?.map((roster) => ({
          id: Number(roster.id),
          name: roster.name,
        })) ?? [],
    }
  }, [props.data])

  return (
    <CardContainer
      to={`/workers/${data.id}`}
      onClick={(e) => {
        if (
          e.target instanceof Element &&
          e.target.closest('button, .modal-content')
        ) {
          e.preventDefault()
          e.stopPropagation()
        }
      }}
    >
      <WorkerCardContent data={data} />
      <Flex width={[1, 'auto']} mt={[3, 0]} ml={['6.4rem', 0]} pl={[3, 0]}>
        <Flex alignItems="center">
          <Button
            variant={'text'}
            onClick={() => setIsRosterSelectorOpen(true)}
          >
            {listText}
          </Button>
        </Flex>

        <AddWorkerToListModal
          worker={data}
          onUpdated={(rosters) => updateWorkerRosters(data.id, rosters)}
          isOpen={isRosterSelectorOpen}
          onClose={() => setIsRosterSelectorOpen(false)}
        />
      </Flex>
    </CardContainer>
  )
}
